<script setup lang="ts">
import { isAfter, isBefore, isEqual, isToday } from 'date-fns'

const {
  day,
  date = null,
  isRangePicker = false,
  rangeStep = undefined,
  rangeStartDate = null,
  rangeEndDate = null,
  minDate = null,
  startRangeTitle = 'Příjezd',
  endRangeTitle = 'Odjezd',
  selectedTitle = 'Výběr',
  isFullRangeSelected = false,
  todayTitle = 'Dnes',
  hoveredDate = null,
} = defineProps<{
  day: number
  date?: Date
  isRangePicker?: boolean
  rangeStep?: 'start' | 'end'
  rangeStartDate?: Date
  rangeEndDate?: Date
  minDate?: Date
  startRangeTitle?: string
  endRangeTitle?: string
  selectedTitle?: string
  isFullRangeSelected?: boolean
  todayTitle?: string
  hoveredDate?: Date
}>()

const emit = defineEmits(['hoverDate'])

// Highlight dates inside a range between selected date and selectable & hovered date
function isHoverRange(): boolean {
  if (!isRangePicker || !hoveredDate)
    return false

  if (rangeStep === 'start' && minDate && isBefore(hoveredDate, minDate))
    return false

  if (rangeStep === 'start' && rangeEndDate)
    return (isBefore(date, rangeEndDate) || isEqual(date, rangeEndDate)) && isAfter(date, hoveredDate)

  if (rangeStep === 'end' && rangeStartDate)
    return (isAfter(date, rangeStartDate) || isEqual(date, rangeStartDate)) && isBefore(date, hoveredDate)

  return false
}

function isInsideRange(): boolean {
  if (!isRangePicker || !date || !rangeEndDate || !rangeStartDate)
    return false

  return isBefore(date, rangeEndDate) && isAfter(date, rangeStartDate)
}

function isEndRange(): boolean {
  if (!isRangePicker || !date || !rangeEndDate)
    return false

  return isEqual(date, rangeEndDate)
}

function isStartRange(): boolean {
  if (!isRangePicker || !date || !rangeStartDate)
    return false

  return isEqual(date, rangeStartDate)
}

function getDateTitle(): string {
  if (!date)
    return ''
  if (isStartRange())
    return startRangeTitle
  if (isEndRange())
    return endRangeTitle

  return selectedTitle
}

function isDateSelectable(): boolean {
  if (!isRangePicker || isFullRangeSelected)
    return false
  if (!rangeStartDate && !rangeEndDate)
    return false

  const isAfterStart = rangeStartDate && rangeStep === 'end' && isAfter(date, rangeStartDate)

  const isBeforeEnd = rangeEndDate && rangeStep === 'start' && isBefore(date, rangeEndDate)

  return isAfterStart || isBeforeEnd
}
</script>

<template>
  <div
    class="day__cell-inner"
    :data-test-id="`datePicker|${day}`"
    :class="[
      { 'day__inside-range': isInsideRange() },
      { 'dp__active_date day__end-range': isEndRange() },
      { 'dp__active_date day__start-range': isStartRange() },
      { day__selectable: isDateSelectable() },
      { range: isHoverRange() },
      { 'day__start-end': isEndRange() && isStartRange() },
    ]"
    @mouseover="isRangePicker && emit('hoverDate', date)"
    @mouseout="isRangePicker && emit('hoverDate', undefined)"
  >
    <div v-if="day" class="flex flex-col justify-center">
      <span
        v-if="isToday(date) && !isEndRange() && !isStartRange()"
        class="day__today mb-[1px] font-normal"
      >{{ todayTitle }}</span>

      <span v-if="isRangePicker && isEndRange() && isStartRange()" class="day__selected text-[10px] mb-[1px] font-normal">
        <span class="block">{{ startRangeTitle }}</span>
        <span class="block">{{ endRangeTitle }}</span>
      </span>

      <span v-if="!isRangePicker || !isEndRange() || !isStartRange() || isEndRange() !== isStartRange()" class="day__selected text-[10px] mb-[1px] font-normal">
        {{ getDateTitle() }}
      </span>
      <span>{{ day }}</span>
    </div>
  </div>
</template>
